import { ReactElement } from 'react';
import { Row, Table } from '@tanstack/react-table';

import { Tr, TrProps } from '@oaknorth/component-core-v2';

import { flexRender, renderCellContents } from '../../flexRender';
import { TableCellProps } from '../../types';
import { allParentColumnsVisible } from '../all-parent-columns-visible';
import { TableCell } from '../TableCell';

interface TableRowProps<T extends object = {}> extends Omit<TrProps, 'onClick'> {
  table: Table<T>;
  /**
   * Row object based on react-table props.
   */
  row: Row<T>;
  /**
   * Render function for sub row when using custom expandable rows (not hierarchical rows)
   */
  renderExpandedRow?: ({ row }: { row: Row<T> }) => ReactElement | null;
  /**
   * Callback to execute when the user click on a row
   */
  onClick?: (row: Row<T>, event: React.MouseEvent) => void;
}

export const TableRow = <T extends object = {}>({
  row,
  onClick,
  renderExpandedRow,
  table,
  ...props
}: TableRowProps<T>) => {
  return (
    <>
      <Tr
        data-testid={`table-row-${row.id}`}
        cursor={onClick ? 'pointer' : undefined}
        // Set background on cells, not the row - override sticky cells' white background
        css={{ _hover: onClick ? { td: { background: 'blue.50' } } : undefined }}
        onClick={(event) => onClick?.(row, event)}
        // This allows cell contents to trigger particular styles when the row is hovered
        data-group
        {...props}
      >
        {row.getVisibleCells().map((cell) => {
          const { tableCell } = cell.column.columnDef.meta || {};

          const areAllParentColumnsVisible = allParentColumnsVisible(cell.column, table);
          if (!areAllParentColumnsVisible) {
            return null;
          }

          if (typeof tableCell !== 'undefined') {
            return flexRender(tableCell, { key: cell.column.id, cell } satisfies TableCellProps<T, unknown>);
          }
          return (
            <TableCell key={cell.column.id} cell={cell}>
              {renderCellContents(cell)}
            </TableCell>
          );
        })}
      </Tr>
      {renderExpandedRow && renderExpandedRow({ row })}
    </>
  );
};
